import type {Contact, OrderFormState} from '@yandex-delivery/form/ui/OrderForm/types'
import {isNull, isUndefined, omitBy, pipe} from '@yandex-delivery/utils'

import {getCoordinates} from './getCoordinates'

export function toFormState(formState: OrderFormState): OrderFormState {
  const route = toRoute(formState)

  const routePoints = toRoutePoints(formState)

  const paymentInfo = formState.payment_info
    ? {
        id: formState.payment_info.method_id,
        type: formState.payment_info.type,
        kind: formState.payment_info.kind,
        has_disable_reason: formState.payment_info.has_disable_reason,
      }
    : {type: 'cargocorp'}

  // Вырезаем явно ненужные поля и оставляем все остальные
  const {
    contacts: _contacts,
    skip_confirmation: _skip_confirmation,
    _loaders,
    due_type,
    due_time,
    due_date,
    _items,
    ...otherFields
  } = formState

  // в конфиге формы грузчики строкой, но ручки принимают числом
  const loaders = _loaders ? {loaders: parseInt(_loaders)} : {}

  const result = {
    ...otherFields,
    ...loaders,
    route:
      route.length !== 0
        ? route
        : [
            // передаем дефолтный route, если в форме ничего не нашлось
            {
              coordinates: getCoordinates('source'),
              type: 'source',
            },
          ],
    due_type,
    payment_info: paymentInfo,
    route_points: routePoints,
    due_time: due_type === 'due' ? due_time : null,
    due_date: due_type === 'due' ? due_date : null,
    items: Array.isArray(route) ? makeDummyItems(route) : [],
  }

  return pipe(result, omitBy(isNull), omitBy(isUndefined))
}

function getIndex(key: string, keyIndex: number, length: number): number {
  if (key === 'source') {
    return 0
  }
  if (key === 'return') {
    return length - 1
  }

  return Number(key.split('_')[1]) || keyIndex
}

const item = {
  title: 'Другое',
  name: 'Другое',
  cost_value: '0.00',
  cost_currency: 'RUB',
  quantity: 1,
  count: 1,
  pickup_point: 0,
}

function makeDummyItems(route: {type: string; coordinates?: [number, number]; uri?: string}[]) {
  /**
   * при запросах в форму грузового количество и индексы айтемов проверяются прайсингом и клеймами,
   * но юзер нигде не имеет возможности заполнить эти поля.
   * Поэтому список айтемов заполняем дамми-объектами
   * */
  const length = route.filter(routePoint => routePoint.type === 'destination').length
  const array = []

  for (let i = 0; i < length; i++) {
    array.push(Object.assign({}, item, {droppof_point: i + 1, dropoff_point: i + 1}))
  }

  return array
}

export function toRoute(formState: OrderFormState) {
  const route = formState.route

  return route
    ? Object.keys(route).flatMap(key => {
        // если адрес не установлен, не передаем точку
        return route[key]?.address?.coordinates
          ? {
              type: key === 'source' || key === 'return' ? key : 'destination',
              coordinates: route[key].address.coordinates,
              uri: route[key].address.uri,
            }
          : []
      })
    : []
}

export function toRoutePoints(formState: OrderFormState) {
  const returnPoint = formState.route?.return?.address

  let routePoints = formState.route
    ? Object.keys(formState.route).flatMap((key, keyIndex) => {
        const index = getIndex(key, keyIndex, Object.keys(formState.route).length)
        const {address} = formState.route[key] || {}

        if (!address) {
          return []
        }

        const mappedAddress = {
          address: {
            fullname: address.title,
            shortname: address.title,
            coordinates: address.coordinates,
            uri: address.uri,
            country: address.country,
            city: address.city,
            building: address.building,
            street: address.street,
            apartment: address.apartment,
            entrance: address.entrance,
            floor: address.floor ? Number(address.floor) : undefined,
            code: address.code,
            comment: address.comment,
          },
          point_id: index,
          contact:
            key === 'return'
              ? {
                  phone: returnPoint.phone,
                  route_index: index,
                  name: formState.contacts[0].name,
                }
              : {...formState.contacts?.find((contact: Contact) => contact.pointId === key), route_index: index},
          type: key === 'source' || key === 'return' ? key : 'destination',
          visit_order: index + 1,
          skip_confirmation: key === 'return' ? formState.skip_confirmation : null,
        }

        return pipe(mappedAddress, omitBy(isNull), omitBy(isUndefined))
      })
    : []

  // фильтр - потому что в data может приехать return с пустым адресом,
  // сорт - потому что ручке принятия заказа принципиален порядок точек в route_points
  routePoints = routePoints
    .filter(point => !!point.address?.fullname)
    ?.sort((point1, point2) => point1.point_id - point2.point_id)

  return routePoints
}
